import {
  QBox,
  QDotSeparator,
  QFlex,
  QPerson,
  QStack,
  QText,
} from '@qualio/ui-components';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../../../../hooks/useFormatDate';
import { convertEpochToDate } from '../../../../../util/DateUtils';
import { DocumentOverviewContext } from '../../Context';
import { DocumentUsersProps } from './types';
import { getDocumentUserProps } from './utils';

export const DocumentUsers = ({ userType, users = [] }: DocumentUsersProps) => {
  const { t } = useTranslation();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const formatDate = useFormatDate();
  const sortedUsers = useMemo(
    () => users.sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [users],
  );

  if (!sortedUsers.length) {
    return (
      <QStack mt={3}>
        <QText>{t('labels.none')}</QText>
      </QStack>
    );
  }

  return (
    <QStack spacing={3} mt={3}>
      {sortedUsers.map((user) => {
        const { status, timestamp, comment } = getDocumentUserProps({
          userType,
          qualioDocument,
          user,
        });
        if (status === 'pending' || status === 'default' || !timestamp) {
          return <QPerson fullName={user.full_name} status={status} />;
        } else if (userType === 'editor') {
          return (
            <QStack spacing={1} key={`${user.id}-${status}`}>
              <QPerson status={status} fullName={user.full_name} />
              <QFlex direction="row" alignItems="center">
                <QBox width="16px" mr="8px" />
                <QText fontSize="sm" color="gray.500">
                  {t('labels.finishedEditing')}
                </QText>
                <QBox mx={1}>
                  <QDotSeparator />
                </QBox>
                <QText fontSize="sm" color="gray.500">
                  {formatDate(convertEpochToDate(timestamp), true)}
                </QText>
              </QFlex>
            </QStack>
          );
        } else {
          return (
            <QStack spacing={1}>
              <QPerson
                key={`${user.id}-${status}`}
                status={status}
                fullName={user.full_name}
                actionTimestamp={formatDate(
                  convertEpochToDate(timestamp),
                  true,
                )}
              />
              {comment && (
                <QText as="i" fontSize="xs" mt={1} color={'gray.700'}>
                  {comment}
                </QText>
              )}
            </QStack>
          );
        }
      })}
    </QStack>
  );
};
